/**
 * Step Navigation Utility
 * Handles navigation between different steps in the due diligence process
 */

export interface StepNavigationInfo {
  previousStepUrl: string;
  previousStepText: string;
  canGoBack: boolean;
}

export type CurrentPage = 
  | 'register-deal'
  | 'document-upload' 
  | 'ai-review'
  | 'report-generation';

/**
 * Get navigation information for the previous step
 * @param currentPage - The current page the user is on
 * @param dealId - The deal ID (optional, used for step URLs)
 * @returns Navigation information for the previous step
 */
export const getPreviousStepNavigation = (
  currentPage: CurrentPage,
  dealId?: string
): StepNavigationInfo => {
  switch (currentPage) {
    case 'register-deal':
      // Register deal is the starting point, go back to deals list
      return {
        previousStepUrl: '/deals',
        previousStepText: 'Back to Deals',
        canGoBack: true
      };

    case 'document-upload':
      // Document upload is step 1, go back to deals list
      return {
        previousStepUrl: '/deals',
        previousStepText: 'Back to Deals',
        canGoBack: true
      };

    case 'ai-review':
      // AI review is step 2, go back to document upload (step 1)
      return {
        previousStepUrl: dealId ? `/document-upload?dealId=${dealId}` : '/deals',
        previousStepText: 'Back to Document Upload',
        canGoBack: true
      };

    case 'report-generation':
      // Report generation is step 3, go back to AI review (step 2)
      return {
        previousStepUrl: dealId ? `/ai-review?dealId=${dealId}` : '/deals',
        previousStepText: 'Back to AI Review',
        canGoBack: true
      };

    default:
      // Fallback to deals page
      return {
        previousStepUrl: '/deals',
        previousStepText: 'Back to Deals',
        canGoBack: true
      };
  }
};

/**
 * Get the current step number based on the page
 * @param currentPage - The current page
 * @returns Step number (1-3)
 */
export const getCurrentStepNumber = (currentPage: CurrentPage): number => {
  switch (currentPage) {
    case 'register-deal':
      return 0; // Pre-step
    case 'document-upload':
      return 1;
    case 'ai-review':
      return 2;
    case 'report-generation':
      return 3;
    default:
      return 1;
  }
};

/**
 * Get step name for display
 * @param stepNumber - The step number
 * @returns Human-readable step name
 */
export const getStepName = (stepNumber: number): string => {
  switch (stepNumber) {
    case 0:
      return 'Deal Registration';
    case 1:
      return 'Document Upload';
    case 2:
      return 'AI Review';
    case 3:
      return 'Report Generation';
    default:
      return 'Unknown Step';
  }
};
